import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { API_DETAILS } from "../../App";
import "../../styles/Doctor-ui/SymptomAssessmentSummary.css";

/**
 * The patient's symptom assessments, listed newest first as collapsed rows so
 * they sit alongside whatever else the Results tab grows to hold. Each row shows
 * the diagnosis, the score and when it was completed; opening one reveals every
 * question with the answer the patient gave.
 *
 * Answers are stored per submission as a JSON string in questionData:
 * [{ qId, question, optionsData: [{ oId, oName }] }, ...], where optionsData
 * holds the options the patient chose.
 */

// DESIGN_SYSTEM.md §2 bands. The severity palette exists for symptom scores and
// nothing else, so this is the one place it belongs.
const scoreBand = (score) => {
  if (score >= 90) return { label: "Mildly Bothersome", cls: "sas-mild" };
  if (score >= 60) return { label: "Moderately Bothersome", cls: "sas-moderate" };
  if (score >= 30) return { label: "Very Bothersome", cls: "sas-high" };
  return { label: "Severe", cls: "sas-severe" };
};

const answerText = (item) => {
  const options = Array.isArray(item.optionsData) ? item.optionsData : [];
  const names = options.map((o) => o && o.oName).filter(Boolean);
  return names.length > 0 ? names.join(", ") : null;
};

const SymptomAssessmentSummary = ({ patientId }) => {
  const [assessments, setAssessments] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const base =
      API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Scores carry only diagnosisId, so the names come from the patient's
        // diagnosis list. A failure there costs the title, not the assessment.
        const [scoreRes, diagRes] = await Promise.all([
          axios.get(`${base}/symptomScore/${patientId}`),
          axios
            .get(`${base}/getDiagnosisByCustomer/${patientId}`)
            .catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const diagNames = {};
        (Array.isArray(diagRes.data) ? diagRes.data : []).forEach((d) => {
          if (d && d.diagId != null) diagNames[d.diagId] = d.diagName;
        });

        const scores = Array.isArray(scoreRes.data) ? scoreRes.data : [];
        const parsed = scores
          .map((s) => {
            let answers = [];
            let unreadable = false;
            try {
              const raw = JSON.parse(s.questionData || "[]");
              answers = Array.isArray(raw) ? raw : [];
            } catch (e) {
              // One bad blob should not take out the rest of the list.
              unreadable = true;
            }
            const name = diagNames[s.diagnosisId];
            return {
              id: s.scoreId,
              score: s.symptomScore,
              completedAt: s.dateTime,
              title: name
                ? `${name} Symptom Assessment`
                : "Symptom Assessment",
              answers,
              unreadable,
            };
          })
          .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

        setAssessments(parsed);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.errorMessage ||
            "Could not load symptom assessments."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (loading) {
    return <p className="sas-status">Loading symptom assessments…</p>;
  }

  if (error) {
    return <p className="sas-status sas-error">{error}</p>;
  }

  if (assessments.length === 0) {
    return (
      <p className="sas-status">
        This patient has not completed a symptom assessment yet.
      </p>
    );
  }

  return (
    <section className="sas-section" aria-label="Symptom assessments">
      <ul className="sas-rows">
        {assessments.map((a) => {
          const band = scoreBand(a.score);
          const isOpen = openId === a.id;
          return (
            <li className="sas-row" key={a.id}>
              <button
                type="button"
                className="sas-row-head"
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? null : a.id)}
              >
                <span className={"sas-chevron" + (isOpen ? " sas-chevron-open" : "")}>
                  ›
                </span>
                <span className="sas-row-main">
                  <span className="sas-row-title">{a.title}</span>
                  <span className="sas-row-date">
                    Completed{" "}
                    {a.completedAt
                      ? moment(a.completedAt).format("MMMM Do YYYY, h:mm A")
                      : "—"}
                  </span>
                </span>
                <span className={"sas-row-score " + band.cls}>
                  <span className="sas-score-value">{a.score}</span>
                  <span className="sas-score-label">{band.label}</span>
                </span>
              </button>

              {isOpen && (
                <div className="sas-row-body">
                  {a.unreadable ? (
                    <p className="sas-status sas-error">
                      This assessment's answers could not be read.
                    </p>
                  ) : a.answers.length === 0 ? (
                    <p className="sas-status">
                      No answers were recorded for this assessment.
                    </p>
                  ) : (
                    <ol className="sas-list">
                      {a.answers.map((item, i) => {
                        const text = answerText(item);
                        return (
                          <li className="sas-item" key={item.qId ?? i}>
                            <p className="sas-question">
                              {item.question || `Question ${i + 1}`}
                            </p>
                            {text ? (
                              <p className="sas-answer">{text}</p>
                            ) : (
                              <p className="sas-answer sas-answer-empty">
                                Not answered
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SymptomAssessmentSummary;
